import { LayoutArea, PanelConfig, ResponsiveMode, ResponsivePanelConfig } from './types';

// Responsive breakpoints for viewport detection
export const RESPONSIVE_BREAKPOINTS = {
  mobile: 768, // Below 768px = mobile
  tablet: 1024, // 768px - 1024px = tablet
  desktop: 1025, // Above 1024px = desktop
} as const;

// Enhanced panel configuration with pixel-aware sizing for 5 core areas
export const DEFAULT_PANEL_CONFIG: Record<LayoutArea, PanelConfig> = {
  [LayoutArea.HEADER]: {
    defaultSize: 8, // ~60px at 800px viewport height (60/800 * 100 = 7.5%, rounded to 8%)
    minSize: 6, // ~48px min
    maxSize: 12, // ~80px max
    collapsible: false,
    resizable: false, // Header is fixed height
    // Pixel constraints for content-aware sizing
    minPixels: 48, // Minimum 48px height
    maxPixels: 80, // Maximum 80px height
    autoSize: 'fixed', // Fixed height header
    collapseSize: 0, // Headers don't collapse
  },
  [LayoutArea.LEFT_SIDEBAR]: {
    defaultSize: 20, // 20% of available width
    minSize: 15, // 15% minimum
    maxSize: 40, // 40% maximum
    collapsible: true,
    resizable: true,
    // Pixel constraints for usable sidebar
    minPixels: 200, // Minimum 200px width for usable sidebar
    maxPixels: 400, // Maximum 400px to not dominate screen
    autoSize: 'content', // Size based on navigation content
    collapseSize: 40, // Collapsed to icon-only width
    collapseThreshold: 150, // Auto-collapse below 150px
  },
  [LayoutArea.CONTENT]: {
    defaultSize: 100, // Takes remaining space (react-resizable-panels handles this)
    minSize: 30, // Minimum 30% of space
    maxSize: 100, // Can expand to full space
    collapsible: false,
    resizable: false, // Content takes remaining space automatically
    // Pixel constraints for usable content area
    minPixels: 400, // Minimum 400px width for content readability
    autoSize: 'flexible', // Flexible sizing to use available space
    collapseSize: 0, // Content never collapses
  },
  [LayoutArea.RIGHT_SIDEBAR]: {
    defaultSize: 25, // 25% of available width
    minSize: 20, // 20% minimum
    maxSize: 50, // 50% maximum
    collapsible: true,
    resizable: true,
    // Pixel constraints for auxiliary content
    minPixels: 200, // Minimum 200px width
    maxPixels: 350, // Maximum 350px for auxiliary content
    autoSize: 'content', // Size based on auxiliary content
    collapseSize: 0, // Fully collapsed when not needed
    collapseThreshold: 180, // Auto-collapse below 180px
  },
  [LayoutArea.FOOTER]: {
    defaultSize: 6, // ~48px at 800px viewport height
    minSize: 5, // ~40px min
    maxSize: 8, // ~56px max
    collapsible: true,
    resizable: false, // Footer is fixed height
    // Pixel constraints for status information
    minPixels: 40, // Minimum 40px height
    maxPixels: 60, // Maximum 60px height
    autoSize: 'fixed', // Fixed height footer
    collapseSize: 0, // Can be hidden completely
    collapseThreshold: 35, // Auto-collapse below 35px
  },
  // Additional layout areas - minimal configs
  [LayoutArea.BOTTOM_PANEL]: {
    defaultSize: 25,
    minSize: 10,
    maxSize: 50,
    collapsible: true,
    resizable: true,
    minPixels: 100,
    maxPixels: 400,
    autoSize: 'content',
    collapseSize: 0,
  },
  [LayoutArea.TOP_PANEL]: {
    defaultSize: 15,
    minSize: 8,
    maxSize: 30,
    collapsible: true,
    resizable: true,
    minPixels: 60,
    maxPixels: 200,
    autoSize: 'content',
    collapseSize: 0,
  },
  [LayoutArea.TABS]: {
    defaultSize: 6,
    minSize: 4,
    maxSize: 10,
    collapsible: false,
    resizable: false,
    minPixels: 32,
    maxPixels: 60,
    autoSize: 'fixed',
    collapseSize: 0,
  },
  [LayoutArea.SECONDARY_TABS]: {
    defaultSize: 5,
    minSize: 3,
    maxSize: 8,
    collapsible: true,
    resizable: false,
    minPixels: 24,
    maxPixels: 48,
    autoSize: 'fixed',
    collapseSize: 0,
  },
  [LayoutArea.ACTION_BUTTONS]: {
    defaultSize: 8,
    minSize: 6,
    maxSize: 12,
    collapsible: true,
    resizable: false,
    minPixels: 40,
    maxPixels: 80,
    autoSize: 'content',
    collapseSize: 0,
  },
  [LayoutArea.BREADCRUMB]: {
    defaultSize: 4,
    minSize: 3,
    maxSize: 8,
    collapsible: true,
    resizable: false,
    minPixels: 24,
    maxPixels: 60,
    autoSize: 'content',
    collapseSize: 0,
  },
  [LayoutArea.SUBHEADER]: {
    defaultSize: 6,
    minSize: 4,
    maxSize: 10,
    collapsible: true,
    resizable: false,
    minPixels: 32,
    maxPixels: 80,
    autoSize: 'content',
    collapseSize: 0,
  },
} as const;

// Default responsive configurations
export const DEFAULT_RESPONSIVE_CONFIG: Record<ResponsiveMode, ResponsivePanelConfig> = {
  [ResponsiveMode.MOBILE]: {
    // On mobile, prioritize content and use overlay/modal for sidebars
    sidebarMode: 'overlay',
    compactSpacing: true,
    collapsePanels: [LayoutArea.LEFT_SIDEBAR, LayoutArea.RIGHT_SIDEBAR] as LayoutArea[],
    panelOverrides: {
      [LayoutArea.HEADER]: { defaultSize: 10 }, // Slightly larger header on mobile
      [LayoutArea.FOOTER]: { defaultSize: 8 }, // Slightly larger footer on mobile
    },
  },
  [ResponsiveMode.TABLET]: {
    // On tablet, show left sidebar but collapse right sidebar
    sidebarMode: 'stack',
    compactSpacing: false,
    collapsePanels: [LayoutArea.RIGHT_SIDEBAR] as LayoutArea[],
    panelOverrides: {
      [LayoutArea.LEFT_SIDEBAR]: { defaultSize: 25 }, // Slightly larger on tablet
    },
  },
  [ResponsiveMode.DESKTOP]: {
    // On desktop, show all panels
    sidebarMode: 'stack',
    compactSpacing: false,
    collapsePanels: [] as LayoutArea[],
    panelOverrides: {},
  },
} as const;

/**
 * Get the optimized panel configuration for a specific responsive mode
 */
export function getPanelConfigForMode(mode: ResponsiveMode): Record<LayoutArea, PanelConfig> {
  const baseConfig = { ...DEFAULT_PANEL_CONFIG };
  const responsiveConfig = DEFAULT_RESPONSIVE_CONFIG[mode];

  // Apply responsive overrides
  if (responsiveConfig.panelOverrides) {
    Object.entries(responsiveConfig.panelOverrides).forEach(([area, overrides]) => {
      const layoutArea = area as LayoutArea;
      if (baseConfig[layoutArea]) {
        baseConfig[layoutArea] = {
          ...baseConfig[layoutArea],
          ...overrides,
        };
      }
    });
  }

  return baseConfig;
}
